// ============================================================================
// TEMPORARY - DEMO ONLY
// ============================================================================
// This component is for DEMONSTRATION purposes only and should be removed
// before production deployment. It allows developers to trigger test errors
// to demonstrate the error handling system.
//
// Search for "TEMPORARY - DEMO ONLY" to find all related code for removal.
// ============================================================================

import { useState } from 'react';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, Bug, Network, Shield, Clock, FileWarning, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { ErrorContext } from '@/types/error.types';

interface DeveloperToolsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTriggerError?: (error: Error) => void;
  hasActiveError?: boolean; // Prevent closing when error is shown
}

type ErrorType = 'generic' | 'network' | 'validation' | 'permission' | 'async' | 'boundary';

interface ErrorOption {
  type: ErrorType;
  label: string;
  description: string;
  icon: React.ReactNode;
}

const errorOptions: ErrorOption[] = [
  {
    type: 'generic',
    label: 'Generic Error',
    description: 'Throws a basic JavaScript error',
    icon: <Bug className="h-4 w-4" />,
  },
  {
    type: 'network',
    label: 'Network Error',
    description: 'Simulates an API/network failure',
    icon: <Network className="h-4 w-4" />,
  },
  {
    type: 'validation',
    label: 'Validation Error',
    description: 'Simulates form validation failure',
    icon: <FileWarning className="h-4 w-4" />,
  },
  {
    type: 'permission',
    label: 'Permission Error',
    description: 'Simulates unauthorized access',
    icon: <Shield className="h-4 w-4" />,
  },
  {
    type: 'async',
    label: 'Async Error',
    description: 'Simulates promise rejection',
    icon: <Clock className="h-4 w-4" />,
  },
  {
    type: 'boundary',
    label: 'Boundary Error',
    description: 'Triggers React Error Boundary',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
];

export function DeveloperToolsDialog({ open, onOpenChange, onTriggerError, hasActiveError }: DeveloperToolsDialogProps) {
  const [selectedError, setSelectedError] = useState<ErrorType | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleErrorSelect = (errorType: ErrorType) => {
    setSelectedError(errorType);
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    if (!selectedError) return;

    const errorType = selectedError;

    // Don't close dialog - keep it open so it gets blurred with the main app
    setShowConfirmation(false);
    setSelectedError(null);

    // Create the error based on type with proper context
    let error: Error & { appErrorContext?: Partial<ErrorContext> };
    
    switch (errorType) {
      case 'generic':
        error = new Error('Demo: Generic test error triggered from Developer Tools');
        error.appErrorContext = {
          category: 'system',
          severity: 'error',
          suggestedActions: [
            'This is a demonstration error',
            'Try refreshing the page',
            'Contact support if this were a real error'
          ]
        };
        break;
        
            case 'network':
              error = new Error('Demo: Network request failed - Unable to connect to server');
              error.appErrorContext = {
                category: 'network',
                severity: 'error',
                technicalDetails: {
                  statusCode: 503,
                  endpoint: '/api/demo-endpoint',
                  method: 'GET',
                  errorCode: 'NETWORK_ERROR',
                  timestamp: new Date().toISOString(),
                  userAgent: window.navigator.userAgent,
                  url: window.location.href
                },
                suggestedActions: [
                  'Check your internet connection',
                  'The server may be temporarily unavailable',
                  'Try again in a few moments'
                ]
              };
              break;
        
      case 'validation':
        error = new Error('Demo: Validation failed - Invalid data format detected');
        error.appErrorContext = {
          category: 'validation',
          severity: 'warning',
          suggestedActions: [
            'Please check the form for errors',
            'Ensure all required fields are filled correctly',
            'Verify the data format matches requirements'
          ]
        };
        break;
        
            case 'permission':
              error = new Error('Demo: Permission denied - Insufficient privileges to perform this action');
              error.appErrorContext = {
                category: 'authorization',
                severity: 'error',
                technicalDetails: {
                  statusCode: 403,
                  errorCode: 'FORBIDDEN',
                  timestamp: new Date().toISOString(),
                  userAgent: window.navigator.userAgent,
                  url: window.location.href
                },
                suggestedActions: [
                  'You may not have permission to perform this action',
                  'Contact your system administrator for access',
                  'Verify you are logged in with the correct account'
                ]
              };
              break;
        
            case 'async':
              error = new Error('Demo: Async operation failed - Promise rejected unexpectedly');
              error.appErrorContext = {
                category: 'system',
                severity: 'error',
                technicalDetails: {
                  errorCode: 'ASYNC_ERROR',
                  timestamp: new Date().toISOString(),
                  userAgent: window.navigator.userAgent,
                  url: window.location.href
                },
                suggestedActions: [
                  'An asynchronous operation failed',
                  'Try the operation again',
                  'Contact support if the issue persists'
                ]
              };
              break;
        
            case 'boundary':
              error = new Error('Demo: React component error - Boundary test triggered');
              error.appErrorContext = {
                category: 'system',
                severity: 'critical',
                technicalDetails: {
                  errorCode: 'RENDER_ERROR',
                  timestamp: new Date().toISOString(),
                  userAgent: window.navigator.userAgent,
                  url: window.location.href
                },
                suggestedActions: [
                  'A component failed to render',
                  'Try refreshing the page',
                  'Clear your browser cache if the issue persists'
                ]
              };
              break;
        
      default:
        error = new Error('Demo: Unknown error type');
    }

    // Trigger error after dialog closes via callback
    setTimeout(() => {
      if (onTriggerError) {
        onTriggerError(error);
      }
    }, 150);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setSelectedError(null);
  };

  // Prevent drawer from closing when there's an active error
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && hasActiveError) {
      // Don't allow closing the drawer when error is active
      return;
    }
    onOpenChange(newOpen);
  };

  return (
    <Drawer open={open} onOpenChange={handleOpenChange} direction="right">
      <DrawerContent className="h-full w-[400px] sm:w-[500px]">
        <DrawerHeader className="text-left">
          <div className="flex items-center justify-between">
            <DrawerTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-warning" />
              Developer Tools
            </DrawerTitle>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs font-normal text-muted-foreground bg-warning/10 text-warning px-2 py-1 rounded">
              Demo Only
            </span>
          </div>
          <DrawerDescription className="mt-2">
            Trigger test errors to demonstrate the error handling system. This is for development and demonstration purposes only.
          </DrawerDescription>
        </DrawerHeader>

        <ScrollArea className="flex-1 px-4">
          {!showConfirmation ? (
            <div className="space-y-2 py-4">
              <p className="text-sm text-muted-foreground mb-4">Select an error type to trigger:</p>
              {errorOptions.map((option) => (
                <button
                  key={option.type}
                  onClick={() => handleErrorSelect(option.type)}
                  className="w-full flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-accent hover:border-accent-foreground/20 transition-colors text-left"
                >
                  <div className="mt-0.5 text-muted-foreground">{option.icon}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm">{option.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{option.description}</div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-4 py-4">
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Warning:</strong> This will trigger a test error and may disrupt the current page state.
                  The error handling system will catch and display the error.
                </AlertDescription>
              </Alert>

              <div className="p-3 rounded-lg bg-muted">
                <p className="text-sm font-medium mb-1">Selected Error Type:</p>
                <p className="text-sm text-muted-foreground">
                  {errorOptions.find((opt) => opt.type === selectedError)?.label}
                </p>
              </div>
            </div>
          )}
        </ScrollArea>

        <DrawerFooter className="pt-4">
          {!showConfirmation ? (
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full">
              Close
            </Button>
          ) : (
            <div className="flex gap-2 w-full">
              <Button variant="outline" onClick={handleCancel} className="flex-1">
                Back
              </Button>
              <Button variant="destructive" onClick={handleConfirm} className="flex-1">
                Trigger Error
              </Button>
            </div>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
